import { AGGREGATE_SOURCE_LIST_ID, AGGREGATE_SOURCE_SITE_NAME } from "./aggregateRowFields";

/**
 * The "from" text for a copy/move started in the all-documents grid.
 *
 * This must name a LOCATION. The obvious-looking answer — the name of the view the user
 * was looking at, "All Documents" — is not one: it is a UI surface spanning every library
 * on the site, and no selected file lives there. Telling someone they are moving an item
 * "from All Documents" names nothing they could navigate to.
 *
 * The rows carry their own source library, so:
 *   - one source library  -> that library's name, which is exactly where the items are;
 *   - several            -> the site, the nearest place that genuinely contains them all.
 *
 * Deliberately not a list of library names: this renders inside a single-line MessageBar
 * and in a confirm sentence, and a selection spanning six libraries would push the count
 * and the destination instruction out of view.
 */
export const resolveAggregateSourceLabel = (
    items: any[],
    getLibraryDisplayName: (listId: string) => string | undefined,
    siteDisplayName?: string
): string => {
    const sourceListIds = Array.from(new Set(
        (items ?? []).map(item => item?.[AGGREGATE_SOURCE_LIST_ID]).filter(Boolean)
    ));

    // Rows from client-group sites carry their own site's name; the site's own rows carry the
    // current site's. One site across the selection is the nearest place containing them all.
    const siteNames = Array.from(new Set(
        (items ?? []).map(item => item?.[AGGREGATE_SOURCE_SITE_NAME]).filter(Boolean)
    ));
    const rowSite: string | undefined = siteNames.length === 1 ? siteNames[0] : undefined;

    if (sourceListIds.length === 1) {
        // Fall back to the site rather than to "": an unknown library id still means the
        // items are somewhere on that site, and an empty label would silently drop the
        // "from X" clause out of the confirm sentence entirely.
        return getLibraryDisplayName(sourceListIds[0]) || rowSite || siteDisplayName || "";
    }

    return rowSite || siteDisplayName || "";
};
